// Core Avatar component, orchestrates all avatar systems through focused hooks

import {
  useId,
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { motion } from 'framer-motion';
import colors from 'tailwindcss/colors';
import AvatarMenu from './AvatarMenu';
import {
  useAvatarExpression,
  useAvatarFaceBehavior,
  useAvatarMovement,
  useAvatarBgEffects,
  useAvatarReactions,
} from './hooks';
import { useAnyDialogOpen } from '@/components/ui/dialogPresenceStore';
import { getAnimalAssetUrl } from './utilities/animalAvatarAssets';
import { Emotion } from '@airhead-games/shared/configs';
import { FACE_BOX, HEAD_MASK } from './configs/balloon';
import type { ReactionType } from './configs/reactions';
import { RoomPlayer } from '@airhead-games/shared/types';
import {
  playerColorVars,
  resolvePlayerPalette,
} from '../../styles/playerColors';
import { analyticsService } from '../../services/analyticsService';

/**
 * Transform data for Avatar positioning and animation
 */
export interface AvatarTransforms {
  position: { x: number; y: number };
  rotation: { x: number; y: number; z: number };
  headTransform: {
    x: number;
    y: number;
    rotation: number;
    scale: number;
  };
  faceTransforms: {
    leftEye: {
      shiftX: number;
      shiftY: number;
      scale: number;
      rotation: number;
    };
    rightEye: {
      shiftX: number;
      shiftY: number;
      scale: number;
      rotation: number;
    };
    mouth: { shiftX: number; shiftY: number; scale: number; rotation: number };
  };
}

type AvatarContainerStyle = React.CSSProperties & {
  '--avatar-size'?: string;
};

/**
 * Props for the Avatar component
 */
export interface AvatarProps {
  player?: RoomPlayer; // Player data for colors and saved expressions
  emotion?: Emotion; // Current emotional state
  allowWaitingEmotionOverride?: boolean; // Whether waiting-for-input can override the provided emotion
  size?: number | string; // Avatar size in pixels or CSS unit
  topLabel?: string; // Optional text shown above the avatar
  bottomLabel?: string; // Optional text shown below the avatar
  topLabelColor?: string; // Optional color override for top label (CSS color)
  bottomLabelColor?: string; // Optional color override for bottom label (CSS color)
  topLabelStyle?: React.CSSProperties; // Optional custom styles for top label (color, animation, etc)
  bottomLabelStyle?: React.CSSProperties; // Optional custom styles for bottom label (color, animation, etc)
  topLabelAnimationKey?: string | number; // Optional key that triggers animation when changed (for bounce animation)
  topLabelAnimationType?: 'bounce'; // Animation style for top label changes ('bounce' triggers on key change)
  topLabelState?: 'normal' | 'scaled'; // State for scale animation - 'scaled' shows scaled/flashing effect
  enableBlinking?: boolean; // Whether natural blinking is enabled
  enableMouthMovement?: boolean; // Whether natural mouth movements are enabled
  enableFloat?: boolean; // Whether floating/physics movement is enabled
  isReacting?: boolean; // Whether reaction animation is active
  reactionType?: ReactionType; // Type of reaction to perform
  enableGlow?: boolean; // Whether glow effect is enabled
  glowType?: 'pulse' | 'static'; // Preferred glow animation type
  isPoppy?: boolean; // Whether this is Poppy (special behavior)
  disableMenu?: boolean; // Whether to disable the hover menu for Poppy
  onHoverStateChange?: (isHovered: boolean) => void; // Callback to expose hover state changes
  forceHoveredAppearance?: boolean; // Force hover appearance (surprised eyes, big mouth for Poppy)
  customExpression?: { eyeShape?: string; mouthShape?: string }; // Custom expression override (now shape-based)
  scoreFace?: 'off' | 'turn' | 'game'; // Sets facial expression based on score standings
  labelFadeInDelay?: number; // Seconds to delay label fade-in (used to hide labels during entrance). Captured at mount; later changes are ignored.
  transformRef?: React.RefObject<AvatarTransforms>; // Optional ref to expose transform state
  animalStyle?: boolean; /** When true (and not Poppy), render CDN animal art for the player color instead of the balloon SVG. */
  faceOpacity?: number; // Optional opacity applied to the face group
  faceOffsetX?: number; // Optional x offset applied to the face group
  faceOffsetY?: number; // Optional y offset applied to the face group
  faceScale?: number; // Optional scale applied to the face group
  faceRotation?: number; // Optional rotation applied to the face group
}

/**
 * Avatar Component
 *
 * Renders an animated balloon avatar using the new hook-based architecture.
 * Coordinates multiple animation systems for a cohesive, lifelike result.
 *
 * @param props - Avatar configuration
 * @returns Rendered avatar with all animation systems
 */
export default function Avatar({
  player,
  emotion = 'happy',
  allowWaitingEmotionOverride = true,
  size = 240,
  topLabel,
  bottomLabel,
  topLabelColor,
  bottomLabelColor,
  topLabelStyle,
  bottomLabelStyle,
  topLabelAnimationKey,
  topLabelAnimationType = 'bounce',
  topLabelState,
  enableBlinking = true,
  enableMouthMovement = true,
  enableFloat = true,
  isReacting = false,
  reactionType,
  enableGlow,
  glowType,
  isPoppy = false,
  disableMenu = false,
  onHoverStateChange,
  forceHoveredAppearance = false,
  customExpression,
  scoreFace = 'off',
  labelFadeInDelay = 0,
  transformRef,
  animalStyle = false,
  faceOpacity = 1,
  faceOffsetX = 0,
  faceOffsetY = 0,
  faceScale = 1,
  faceRotation = 0,
}: AvatarProps) {
  const uid = useId();
  const leftEyeRef = useRef<SVGPathElement>(null);
  const rightEyeRef = useRef<SVGPathElement>(null);
  const mouthRef = useRef<SVGPathElement>(null);

  // State management
  const [isHovered, setIsHovered] = useState(false);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const isGlobalAnimationPaused = useAnyDialogOpen();

  const [isLabelAnimating, setIsLabelAnimating] = useState(false);
  const previousAnimationKeyRef = useRef<string | number | undefined>(
    undefined
  );

  // Lock in the entrance fade-in delay at mount. Re-renders that change
  // labelFadeInDelay (e.g. shouldAnimate flipping) cannot retroactively
  // shorten or extend this player's label fade-in timing.
  const [lockedLabelFadeInDelay] = useState(labelFadeInDelay);
  // The label motion.div may mount well after the Avatar mounts (e.g. a
  // top label that only appears mid-game). The fade-in delay is measured
  // from Avatar mount, so compute the remaining delay at render time —
  // labels that first appear after the entrance window get no delay.
  const avatarMountedAtRef = useRef<number>(
    typeof performance !== 'undefined' ? performance.now() : Date.now()
  );
  const getEffectiveLabelFadeInDelay = (): number => {
    if (lockedLabelFadeInDelay <= 0) return 0;
    const now =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    const elapsed = (now - avatarMountedAtRef.current) / 1000;
    return Math.max(0, lockedLabelFadeInDelay - elapsed);
  };

  // Calculate display size
  const pxSize = typeof size === 'number' ? `${size}px` : size;
  const avatarSizeVar = typeof size === 'number' ? `${size}px` : undefined; // used for clamp sizing

  // HOOK ORCHESTRATION
  // ==================

  // 5. Reaction Animations - game-triggered emotional responses
  const { headTransform } = useAvatarReactions({
    isReacting: isReacting && !isGlobalAnimationPaused,
    reactionType,
  });

  // 6. Hover face transforms (like original) - Poppy eye shifts when hovered
  // effectiveHovered allows forcing hover appearance (e.g., for music player paused state)
  const effectiveHovered = forceHoveredAppearance || isHovered;
  const hoverTransforms =
    isPoppy && effectiveHovered && !isReacting
      ? {
          leftEye: { shiftX: -4, shiftY: -8, scale: 1, rotation: 0 },
          rightEye: { shiftX: 4, shiftY: -8, scale: 1, rotation: 0 },
          mouth: { shiftX: 0, shiftY: 0, scale: 1, rotation: 0 },
        }
      : {
          leftEye: { shiftX: 0, shiftY: 0, scale: 1, rotation: 0 },
          rightEye: { shiftX: 0, shiftY: 0, scale: 1, rotation: 0 },
          mouth: { shiftX: 0, shiftY: 0, scale: 1, rotation: 0 },
        };

  // 1. Expression Selection - determines which face to show with hover transforms
  const { eyeExpression, mouthExpression, faceTransforms, resolvedEmotion } =
    useAvatarExpression({
      player,
      emotion,
      allowWaitingEmotionOverride,
      customExpression,
      isHovered: effectiveHovered,
      isReacting,
      isPoppy,
      scoreFace,
      reactionTransforms: hoverTransforms,
    });

  const animalSrc = useMemo(() => {
    if (!animalStyle || isPoppy || !player) return null;
    return getAnimalAssetUrl(player.playerColor, resolvedEmotion);
  }, [animalStyle, isPoppy, player, resolvedEmotion]);

  const animalIdentity =
    animalStyle && !isPoppy && player ? player.playerColor : null;
  const displayedAnimalIdentityRef = useRef<string | null>(animalIdentity);
  const [displayedAnimalSrc, setDisplayedAnimalSrc] = useState<string | null>(
    animalSrc
  );

  useEffect(() => {
    if (!animalSrc || !animalIdentity) {
      displayedAnimalIdentityRef.current = null;
      setDisplayedAnimalSrc(null);
      return;
    }

    const previousAnimalIdentity = displayedAnimalIdentityRef.current;
    displayedAnimalIdentityRef.current = animalIdentity;

    if (
      !displayedAnimalSrc ||
      previousAnimalIdentity !== animalIdentity ||
      displayedAnimalSrc === animalSrc
    ) {
      setDisplayedAnimalSrc(animalSrc);
      return;
    }

    let isCancelled = false;
    const nextAnimalImage = new Image();

    nextAnimalImage.onload = () => {
      if (!isCancelled) {
        setDisplayedAnimalSrc(animalSrc);
      }
    };

    nextAnimalImage.onerror = () => {
      // Keep the current image visible if the replacement asset cannot be fetched.
    };

    nextAnimalImage.src = animalSrc;

    return () => {
      isCancelled = true;
      nextAnimalImage.onload = null;
      nextAnimalImage.onerror = null;
    };
  }, [animalIdentity, animalSrc, displayedAnimalSrc]);

  const showAnimalImage = Boolean(
    animalStyle && !isPoppy && player && displayedAnimalSrc
  );

  // 2. Natural Face Behavior - blinking and mouth movements
  const { triggerBlink } = useAvatarFaceBehavior({
    eyeExpression: eyeExpression.shapeName,
    mouthExpression: mouthExpression.shapeName,
    enableBlinking:
      enableBlinking &&
      resolvedEmotion !== 'dead' &&
      !showAnimalImage &&
      !isGlobalAnimationPaused, // Disable blinking for dead emotion, animals, and menu pause
    // Disable natural mouth closing for Poppy and dead emotion
    enableMouthClosing:
      enableMouthMovement &&
      !isPoppy &&
      resolvedEmotion !== 'dead' &&
      !showAnimalImage &&
      !isGlobalAnimationPaused,
    isPoppy,
    eyeRefs: { left: leftEyeRef, right: rightEyeRef },
    mouthRef: mouthRef,
  });

  // Pause local float/glow when the avatar menu is open to reduce visual jitter behind the modal.
  const shouldFloat =
    enableFloat && !isHovered && !isAvatarMenuOpen && !isGlobalAnimationPaused;
  const shouldGlow =
    enableGlow && !isAvatarMenuOpen && !isGlobalAnimationPaused;

  // 3. Spatial Movement - physics and 3D positioning
  const { position, rotation } = useAvatarMovement({
    enableFloat: shouldFloat,
    containerSize: 240, // Always use SVG viewBox size, not display size
  });

  // 4. Background Effects - glow and other visual effects
  const { glowScale, glowOpacity, isGlowActive } = useAvatarBgEffects({
    player,
    enableGlow: shouldGlow,
    glowType,
  });

  // Face transforms are pre-calculated by useAvatarExpression (includes mirroring)
  // Set SVG paths from the shape expressions
  useEffect(() => {
    if (leftEyeRef.current) {
      leftEyeRef.current.setAttribute('d', eyeExpression.path);
    }
    if (rightEyeRef.current) {
      rightEyeRef.current.setAttribute('d', eyeExpression.path);
    }
    if (mouthRef.current) {
      mouthRef.current.setAttribute('d', mouthExpression.path);
    }
  }, [eyeExpression.path, mouthExpression.path]);

  // Update transformRef with current transform values for external use
  useEffect(() => {
    if (transformRef) {
      transformRef.current = {
        position,
        rotation,
        headTransform,
        faceTransforms,
      };
    }
  }, [transformRef, position, rotation, headTransform, faceTransforms]);

  // Detect topLabelAnimationKey changes and trigger bounce animation
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    // Trigger animation if key changed (including when going from undefined to a value)
    if (
      topLabelAnimationKey !== undefined &&
      topLabelAnimationKey !== previousAnimationKeyRef.current
    ) {
      // Animation key changed - trigger animation
      setIsLabelAnimating(true);
      const duration = 600; // Bounce animation duration
      timeout = setTimeout(() => {
        setIsLabelAnimating(false);
      }, duration);
    }
    previousAnimationKeyRef.current = topLabelAnimationKey;

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [topLabelAnimationKey, topLabelAnimationType]);

  // INTERACTION HANDLERS
  // ====================

  const handleAvatarClick = useCallback(() => {
    triggerBlink();
    if (isPoppy && isHovered && !disableMenu) {
      analyticsService.trackNavigationMenuToggle('poppy_menu', 'open', {
        surface: 'avatar_menu',
        source_path:
          typeof window !== 'undefined' ? window.location.pathname : undefined,
      });
      setIsAvatarMenuOpen(true);
    }
  }, [isPoppy, isHovered, disableMenu, triggerBlink]);

  const handleAvatarMenuClose = useCallback(() => {
    analyticsService.trackNavigationMenuToggle('poppy_menu', 'closed', {
      surface: 'avatar_menu',
      source_path:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
    setIsAvatarMenuOpen(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!isReacting) {
      setIsHovered(true);
    }
  }, [isReacting]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Expose hover state to parent components when requested
  useEffect(() => {
    if (onHoverStateChange) {
      onHoverStateChange(isHovered);
    }
  }, [isHovered, onHoverStateChange]);

  // COLOR SYSTEM
  // ============

  // Handle player colors with type safety
  const { palette } = resolvePlayerPalette(player);
  const labelColor = palette.light;
  // topLabelStyle.color wins over labelColor when both apply (e.g. MemeMash game over)
  const resolvedTopLabelColor =
    topLabelColor ?? (topLabelStyle?.color as string) ?? labelColor;
  const resolvedBottomLabelColor = bottomLabelColor ?? labelColor;

  // Calculate text length for dynamic font sizing
  const topLabelLength = typeof topLabel === 'string' ? topLabel.length : 0;
  const bottomLabelLength =
    typeof bottomLabel === 'string' ? bottomLabel.length : 0;

  const effectiveLabelFadeInDelay = getEffectiveLabelFadeInDelay();

  const avatarContainerStyle: AvatarContainerStyle = {
    position: 'relative',
    display: 'inline-block',
    width: pxSize,
    height: pxSize,
    // Expose avatar size for clamp-based label sizing.
    '--avatar-size': avatarSizeVar,
  };

  // SVG paths are now set via useEffect from the shape expressions

  return (
    <div style={avatarContainerStyle}>
      {/* Top label positioned at the top edge of the avatar */}
      {topLabel !== undefined && topLabel !== null && (
        <motion.div
          key="top-label"
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            scale: topLabelState === 'scaled' ? 1.4 : 1,
            y:
              topLabelState === 'scaled'
                ? -48
                : topLabelAnimationType === 'bounce' && isLabelAnimating
                  ? [0, -16, 0, -12, 0, -8, 0, -4, 0]
                  : 0,
            color:
              topLabelState === 'scaled'
                ? [
                    resolvedTopLabelColor,
                    colors.neutral[50],
                    palette.light,
                    colors.neutral[50],
                    palette.light,
                  ]
                : resolvedTopLabelColor,
          }}
          transition={
            topLabelState === 'scaled'
              ? {
                  opacity: { duration: 0.3, delay: effectiveLabelFadeInDelay },
                  scale: {
                    duration: 1.0,
                    ease: [0.34, 1.56, 0.64, 1],
                  },
                  y: {
                    duration: 1.0,
                    ease: [0.34, 1.56, 0.64, 1],
                  },
                  color: {
                    duration: 0.5,
                    ease: 'linear',
                    repeat: Infinity,
                    repeatType: 'reverse' as const,
                  },
                }
              : topLabelState === 'normal'
                ? {
                    opacity: {
                      duration: 0.3,
                      delay: effectiveLabelFadeInDelay,
                    },
                    scale: {
                      duration: 1.0,
                      ease: [0.34, 1.56, 0.64, 1],
                    },
                    y: {
                      duration: 1.0,
                      ease: [0.34, 1.56, 0.64, 1],
                    },
                    color: {
                      duration: 1.0,
                      ease: 'linear',
                    },
                  }
                : {
                    opacity: {
                      duration: 0.5,
                      delay: effectiveLabelFadeInDelay,
                    },
                    y: isLabelAnimating
                      ? {
                          duration: 0.5,
                          ease: 'easeOut',
                          times: [0, 0.15, 0.3, 0.45, 0.6, 0.75, 0.9, 0.95, 1],
                        }
                      : { duration: 0 },
                  }
          }
          style={{
            position: 'absolute',
            top: 'clamp(0px, calc(0.02 * var(--avatar-size, 240px)), 8px)',
            left: 0,
            right: 0,
            width: '100%',
            textAlign: 'center',
            fontSize: `clamp(0.5rem, calc(0.2 * var(--avatar-size, 240px) / max(1, ${topLabelLength / 8}) * ${topLabelState === 'scaled' ? 1.2 : 1}), 2.5rem)`,
            lineHeight: 'normal',
            whiteSpace: 'nowrap',
            overflowX: 'hidden',
            overflowY: 'visible',
            textOverflow: 'ellipsis',
            zIndex: 2,
            pointerEvents: 'none',
            userSelect: 'none',
            ...topLabelStyle, // Custom styles override defaults
          }}
        >
          {topLabel}
        </motion.div>
      )}

      {/* Bottom label positioned at the bottom edge of the avatar */}
      {bottomLabel !== undefined && bottomLabel !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: effectiveLabelFadeInDelay }}
          style={{
            position: 'absolute',
            bottom: 'clamp(0px, calc(0.02 * var(--avatar-size, 240px)), 8px)',
            left: 0,
            right: 0,
            width: '100%',
            textAlign: 'center',
            color: resolvedBottomLabelColor,
            fontSize: `clamp(0.5rem, calc(0.2 * var(--avatar-size, 240px) / max(1, ${bottomLabelLength / 8})), 2.5rem)`,
            lineHeight: 'normal',
            whiteSpace: 'nowrap',
            overflowX: 'hidden',
            overflowY: 'visible',
            textOverflow: 'ellipsis',
            zIndex: 2,
            pointerEvents: 'none',
            userSelect: 'none',
            ...bottomLabelStyle, // Custom styles override defaults
          }}
        >
          {bottomLabel}
        </motion.div>
      )}

      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'inline-block',
          overflow: 'visible',
        }}
      >
        <svg
          viewBox="0 0 240 240"
          width="100%"
          height="100%"
          onClick={handleAvatarClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) rotateZ(${rotation.z}deg)`,
            transformStyle: 'preserve-3d',
            cursor: 'pointer',
            pointerEvents: 'auto', // Enable pointer events on the avatar itself
            ...(!isPoppy &&
              !showAnimalImage && {
                ...playerColorVars(palette),
              }),
          }}
          role="img"
          aria-label="Balloon avatar"
        >
          <defs>
            <radialGradient id={`gradient-${uid}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--balloon-fill-light)" />
              <stop offset="40%" stopColor="var(--balloon-fill)" />
              <stop offset="80%" stopColor="var(--balloon-fill-dark)" />
              <stop offset="100%" stopColor={colors.neutral[950]} />
            </radialGradient>

            <radialGradient id={`glow-${uid}`} cx="50%" cy="50%" r="50%">
              <stop
                offset="0%"
                stopColor="var(--glow-color-dark, var(--balloon-fill-dark))"
                stopOpacity="1"
              />
              <stop
                offset="40%"
                stopColor="var(--glow-color, var(--balloon-fill))"
                stopOpacity="0.8"
              />
              <stop
                offset="80%"
                stopColor="var(--glow-color-light, var(--balloon-fill-light))"
                stopOpacity="0.2"
              />
              <stop
                offset="100%"
                stopColor="var(--glow-color-light, var(--balloon-fill-light))"
                stopOpacity="0.0"
              />
            </radialGradient>

            <mask id={`balloon-mask-${uid}`}>
              <path d={HEAD_MASK.balloon} fill="white" />
            </mask>
            <mask id={`stem-mask-${uid}`}>
              <path d={HEAD_MASK.stem} fill="white" />
            </mask>
          </defs>

          {/* Avatar positioned by bouncing physics + head animation transforms */}
          <g transform={`translate(${position.x - 120}, ${position.y - 120})`}>
            <g
              transform={`
                translate(${headTransform.x}, ${headTransform.y})
                translate(120, 120)
                rotate(${headTransform.rotation})
                scale(${headTransform.scale})
                translate(-120, -120)
              `}
            >
              {showAnimalImage && displayedAnimalSrc ? (
                <image
                  href={displayedAnimalSrc}
                  x={0}
                  y={0}
                  width="100%"
                  height="100%"
                  preserveAspectRatio="xMidYMid meet"
                  aria-hidden
                  style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                />
              ) : (
                <>
                  {/* Glow effect - positioned behind balloon and stem */}
                  {isGlowActive && (
                    <circle
                      cx="120"
                      cy="120"
                      r="40"
                      fill={`url(#glow-${uid})`}
                      transform={`scale(${glowScale})`}
                      style={{
                        transformOrigin: '120px 120px',
                        opacity: glowOpacity,
                      }}
                    />
                  )}

                  {/* Balloon body */}
                  <circle
                    cx="112"
                    cy="220"
                    r="48"
                    fill={`url(#gradient-${uid})`}
                    mask={`url(#stem-mask-${uid})`}
                  />
                  <circle
                    cx="96"
                    cy="92"
                    r="120"
                    fill={`url(#gradient-${uid})`}
                    mask={`url(#balloon-mask-${uid})`}
                  />

                  {/* Face group with layered animation transforms */}
                  <g
                    opacity={faceOpacity}
                    transform={`
                      translate(${faceOffsetX}, ${faceOffsetY})
                      translate(100, 110)
                      rotate(${faceRotation})
                      scale(${faceScale})
                      translate(-100, -110)
                    `}
                  >
                    {/* Left Eye */}
                    <g
                      transform={`
                  translate(${FACE_BOX.leftEye.x + faceTransforms.leftEye.shiftX}, ${FACE_BOX.leftEye.y + faceTransforms.leftEye.shiftY})
                  translate(20, 20)
                  rotate(${faceTransforms.leftEye.rotation})
                  scale(${faceTransforms.leftEye.scale})
                  translate(-20, -20)
                `}
                    >
                      <path ref={leftEyeRef} fill={colors.neutral[950]} />
                    </g>

                    {/* Right Eye - mirrored using original transform pattern */}
                    <g
                      transform={`
                  translate(${FACE_BOX.rightEye.x + faceTransforms.rightEye.shiftX}, ${FACE_BOX.rightEye.y + faceTransforms.rightEye.shiftY})
                  translate(20, 20)
                  rotate(${faceTransforms.rightEye.rotation})
                  scale(${faceTransforms.rightEye.scale})
                  translate(-20, -20)
                `}
                    >
                      <g transform="translate(20, 20) scale(-1, 1) translate(-20, -20)">
                        <path ref={rightEyeRef} fill={colors.neutral[950]} />
                      </g>
                    </g>

                    {/* Mouth */}
                    <g
                      transform={`
                  translate(${FACE_BOX.mouth.x + faceTransforms.mouth.shiftX}, ${FACE_BOX.mouth.y + faceTransforms.mouth.shiftY})
                  translate(40, 20)
                  rotate(${faceTransforms.mouth.rotation})
                  scale(${faceTransforms.mouth.scale})
                  translate(-40, -20)
                `}
                    >
                      <path ref={mouthRef} fill={colors.neutral[950]} />
                    </g>
                  </g>
                </>
              )}
            </g>
          </g>
        </svg>

        {/* Only Poppy can open the menu, so only Poppy mounts it — otherwise every
            avatar on a host screen carries its own room/websocket subscriptions. */}
        {isPoppy && !disableMenu && (
          <AvatarMenu
            isOpen={isAvatarMenuOpen}
            onClose={handleAvatarMenuClose}
          />
        )}
      </div>
    </div>
  );
}
